import { reverse, sort } from 'ramda'
import { type Reducer, useCallback, useMemo, useReducer } from 'react'
import { pathStr } from 'utils/fp'
import type { ParsedGridRow } from './useGridRows'

export type OrderDirection = 'asc' | 'desc'

export interface SortingState {
  orderBy: string
  orderDirection: OrderDirection
}

interface SortingReducerAction {
  type: OrderDirection | 'clear' | 'toggle'
  payload?: string
}

export interface GridSortableColumnSpec<V = unknown> {
  key: string
  disableSorting?: boolean
  sortFn?: (prevItemVal: V, nextItemVal: V) => number
}

export interface GridSortingConfig<C extends GridSortableColumnSpec = GridSortableColumnSpec> {
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  disableSorting?: boolean
  controlledSorting?: boolean
  onSortChange?: (sortBy?: string, sortDirection?: 'asc' | 'desc') => void | Promise<void>
  columns: C[]
}

export interface GridSortingProps {
  sortingDisabled: boolean
  sortedBy?: string
  sortedDirection?: OrderDirection
  sortByFieldAsc?: (key: string) => void
  sortByFieldDesc?: (key: string) => void
  clearSorting?: () => void
  toggleSort?: (key: string) => void
}

export const defaultSortWith = (prevValue, nextValue) => {
  const a = String(prevValue ?? '').toLowerCase()
  const b = String(nextValue ?? '').toLowerCase()

  return b < a ? -1 : b > a ? 1 : 0
}

export const defaultSortingState: SortingState = {
  orderBy: null,
  orderDirection: 'asc',
}

const getNextDirection = (sortedDirection) => (sortedDirection === 'asc' ? 'desc' : 'asc')

const sortingReducer: Reducer<SortingState, SortingReducerAction> = (
  { orderDirection, orderBy },
  { type, payload },
) => {
  switch (type) {
    case 'asc':
      return { orderBy: payload ?? orderBy, orderDirection: 'asc' }
    case 'desc':
      return { orderBy: payload ?? orderBy, orderDirection: 'desc' }
    case 'clear':
      return defaultSortingState
    case 'toggle':
    default: {
      if (orderBy !== payload) {
        return { orderBy: payload, orderDirection: 'asc' }
      }
      const nextDirection = getNextDirection(orderDirection)
      return {
        orderBy: orderBy,
        orderDirection: nextDirection,
      }
    }
  }
}

export default function useGridSorting<T, C extends GridSortableColumnSpec>(
  rows: Array<ParsedGridRow<T>>,
  {
    orderBy: initialOrderBy,
    orderDirection: initialOrderDirection,
    disableSorting: disableAllSorting,
    controlledSorting,
    onSortChange,
    columns,
  }: GridSortingConfig<C>,
): [Array<ParsedGridRow<T>>, GridSortingProps] {
  if (disableAllSorting) {
    return [rows, { sortingDisabled: true }]
  }
  const [{ orderBy, orderDirection }, dispatch] = useReducer(sortingReducer, {
    orderBy: initialOrderBy,
    orderDirection: initialOrderDirection,
  })

  // In controlled mode, use prop values for display so external changes
  // (e.g. from a server-side sort dropdown) sync to column header indicators
  const effectiveOrderBy = controlledSorting ? initialOrderBy : orderBy
  const effectiveOrderDirection = controlledSorting ? initialOrderDirection : orderDirection

  const toggleSort = useCallback(
    async (key) => {
      if (controlledSorting) {
        const nextDir = effectiveOrderBy !== key ? 'asc' : getNextDirection(effectiveOrderDirection)
        await onSortChange?.(key, nextDir)
        return
      }
      if (onSortChange) {
        const nextDirection = getNextDirection(orderDirection)
        await onSortChange(key, nextDirection)
      }
      dispatch({ type: 'toggle', payload: key })
    },
    [controlledSorting, effectiveOrderBy, effectiveOrderDirection, orderDirection, onSortChange],
  )
  const sortByFieldAsc = useCallback(
    async (key) => {
      await onSortChange?.(key, 'asc')
      if (!controlledSorting) {
        dispatch({ type: 'asc', payload: key })
      }
    },
    [controlledSorting, onSortChange],
  )
  const sortByFieldDesc = useCallback(
    async (key) => {
      await onSortChange?.(key, 'desc')
      if (!controlledSorting) {
        dispatch({ type: 'desc', payload: key })
      }
    },
    [controlledSorting, onSortChange],
  )
  const clearSorting = useCallback(async () => {
    await onSortChange?.(undefined, undefined)
    if (!controlledSorting) {
      dispatch({ type: 'clear' })
    }
  }, [controlledSorting, onSortChange])

  const sortedRows = useMemo<Array<ParsedGridRow<T>>>(() => {
    if (disableAllSorting || !effectiveOrderBy || controlledSorting) {
      return rows
    }
    const sortByColumn = columns.find(({ key }) => key === effectiveOrderBy)
    if (!sortByColumn) {
      return rows
    }
    const { disableSorting = false, sortFn = defaultSortWith } = sortByColumn
    const sortedRows = disableSorting
      ? rows
      : sort(
          (a, b) => sortFn(pathStr(effectiveOrderBy, b.item), pathStr(effectiveOrderBy, a.item)),
          rows,
        )

    return effectiveOrderDirection === 'desc' ? reverse(sortedRows) : sortedRows
  }, [
    rows,
    columns,
    disableAllSorting,
    controlledSorting,
    effectiveOrderBy,
    effectiveOrderDirection,
  ])

  return [
    sortedRows,
    {
      sortedBy: effectiveOrderBy,
      sortedDirection: effectiveOrderDirection,
      sortByFieldAsc,
      sortByFieldDesc,
      clearSorting,
      toggleSort,
      sortingDisabled: false,
    },
  ]
}
